import { tenantSelector } from '@/features/configuration/configurationSelector';
import { TransText } from '@/i18n/trans/text';
import type { FloorPlanIconsDTO } from '@/types/dto/agent';
import { PlaceType } from '@/types/seatSelection';
import {
  decodeBase64SVG,
  getCalculatedStyleProperties,
  getTenantBrand,
} from '@/utils/color';
import { Button, Icons, Popover, Typography } from '@fleet/webshop-ui';
import type { ComponentType, FC } from 'react';
import { useSelector } from 'react-redux';

interface SeatMapKeyProps {
  floorPlanIcons: FloorPlanIconsDTO | undefined;
}

export const SeatMapKey: FC<SeatMapKeyProps> = ({ floorPlanIcons }) => {
  const tenant = useSelector(tenantSelector);
  const { brandColors } = getTenantBrand(tenant);
  const colors = getCalculatedStyleProperties(brandColors);
  const seatIcon = floorPlanIcons?.floorPlanIcons?.find(
    (icon) => icon.typeId === PlaceType.SEAT
  );
  const seatIconString = seatIcon?.icon?.split(',')[1];

  if (!seatIconString) {
    return null;
  }

  return (
    <Popover
      trigger={
        <Button
          data-testid="seat-map-key-trigger"
          variant="ghostLink"
          className="group font-bold text-primary"
        >
          <TransText i18nKey="seatMapKey" />
          <Icons.chevronDown
            height={16}
            width={16}
            className="transition-transform duration-200 group-aria-expanded:rotate-180"
          />
        </Button>
      }
      content={
        <div className="flex flex-col gap-4">
          <div className="flex items-center gap-2">
            <SeatIconWithText
              iconString={decodeBase64SVG(
                seatIconString,
                colors.white,
                colors.primary
              )}
              text="1"
              textClassName="text-dark"
            />
            <Typography variant="body2" className="text-dark">
              <TransText i18nKey="available" />
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <SeatIconWithText
              iconString={decodeBase64SVG(
                seatIconString,
                colors.cta,
                colors.white
              )}
              text="FL"
              textClassName="text-white"
            />
            <Typography variant="body2" className="text-dark">
              <TransText i18nKey="selected" />
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <SeatIconWithText
              iconString={decodeBase64SVG(
                seatIconString,
                colors.dark,
                colors['neutral-pale']
              )}
              text="CT"
              textClassName="text-white"
            />
            <Typography variant="body2" className="text-dark">
              <TransText i18nKey="coTravelerSeat" />
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <SeatIconWithText
              iconString={decodeBase64SVG(
                seatIconString,
                colors['neutral-pale'],
                colors.neutral
              )}
              overlayIcon={Icons.x}
              overlayIconClassName="text-neutral"
            />
            <Typography variant="body2" className="text-dark">
              <TransText i18nKey="unavailableSeat" />
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <SeatIconWithText
              iconString={decodeBase64SVG(
                seatIconString,
                colors['neutral-pale'],
                colors.neutral
              )}
              overlayIcon={Icons.stop}
              overlayIconClassName="text-neutral h-3 w-3"
            />
            <Typography variant="body2" className="text-dark">
              <TransText i18nKey="seatTypeUnavailable" />
            </Typography>
          </div>
          <div className="flex items-center gap-2">
            <SeatIconWithText
              iconString={decodeBase64SVG(
                seatIconString,
                colors.white,
                colors.primary
              )}
              overlayIcon={Icons.wheelchair}
              overlayIconClassName="text-dark h-3 w-3"
            />
            <Typography variant="body2" className="text-dark">
              <TransText i18nKey="wheelchair" />
            </Typography>
          </div>
        </div>
      }
      placement="bottom start"
      data-testid="seat-map-key-popover"
      className={{ root: 'max-w-[200px] border-neutral-light', content: 'p-4' }}
    />
  );
};

const SeatIconWithText = ({
  iconString,
  text,
  overlayIcon: OverlayIcon,
  textClassName,
  overlayIconClassName,
}: {
  iconString: string;
  text?: string;
  overlayIcon?: ComponentType<{ className?: string }>;
  textClassName?: string;
  overlayIconClassName?: string;
}) => (
  <div className="relative">
    <img
      src={`data:image/svg+xml;base64,${iconString}`}
      width={24}
      alt="seat"
    />

    {(text || OverlayIcon) && (
      <div className="absolute inset-0 flex items-center justify-center">
        {text ? (
          <Typography variant="label" className={textClassName}>
            {text}
          </Typography>
        ) : OverlayIcon ? (
          <OverlayIcon className={overlayIconClassName} />
        ) : null}
      </div>
    )}
  </div>
);
